<template>
	<div class="relative">
		<div class="absolute top-0 right-0 bottom-0 left-0 overflow-hidden">
			<AtomImage
				v-if="backgroundImage"
				v-bind="backgroundImage"
				:width="1920"
				:height="700"
				fit="cover"
				img-class="object-cover w-full h-full"
				preload
			/>
			<AtomVideo
				v-else-if="backgroundVideo"
				v-bind="backgroundVideo"
				class="object-cover w-full h-full"
				autoplay
				loop
				muted
				preload="auto"
				plays-inline
			/>
			<div
				class="absolute top-0 right-0 bottom-0 left-0 from-brand-900 to-brand-900/0 bg-gradient-to-r mix-blend-multiply"
			/>
		</div>
		<div class="container flex flex-col lg:flex-row justify-between gap-12 lg:gap-20">
			<div class="flex flex-col gap-12 lg:gap-20 grow">
				<div class="relative h-[20rem] lg:h-[40rem]">
					<div
						v-for="({ title }, index) in slides"
						:key="`organism-slideshow-${index}`"
						ref="slideRef"
						class="absolute flex items-center h-full w-full"
					>
						<div
							class="grow relative w-full h-full sm:w-3/5 flex flex-col gap-4 justify-end lg:pb-2 drop-shadow-xl"
						>
							<template
								v-for="(texts, textIndex) in title"
								:key="`organism-slideshow-${index}-${textIndex}`"
							>
								<Transition
									:name="
										textIndex % 2 ? 'slide-right' : 'slide-left'
									"
								>
									<span
										v-show="index === currentSlide"
										class="text-nowrap text-title-size-xl sm:text-title-size-2xl lg:text-display-size-md font-display font-extrabold"
									>
										<component
											:is="isBold ? 'strong' : 'span'"
											v-for="{ text, isBold } in texts"
											:key="`organism-slideshow-${index}-${textIndex}-${text}`"
											:class="[
												'-my-2 relative',
												{
													'text-purple-200': isBold,
													'text-white': !isBold,
												},
											]"
										>
											<span class="bg-purple-800 px-4 absolute w-[calc(100%+1rem)] h-[92%] mt-[0.5%] -z-10 -mx-2" />
											{{ text + ' ' }}
										</component>
									</span>
								</Transition>
							</template>
						</div>
					</div>
				</div>

				<div class="pb-16 lg:pb-48 xl:pb-64">
					<slot />
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { ImageType } from '#base/core/types/ImageType';
import type { SlideType } from '#base/core/types/SlideType';
import type { VideoType } from '#base/core/types/VideoType';

const slideInterval = ref<NodeJS.Timeout | string | number | undefined>();
const currentSlide = ref(0);
const slideRef = ref();

defineProps<{
	slides: SlideType[];
	backgroundImage?: ImageType;
	backgroundVideo?: VideoType;
}>();

function setNextSlide() {
	if (slideRef?.value && currentSlide.value + 1 < slideRef.value.length) {
		currentSlide.value++;
	}
	else {
		currentSlide.value = 0;
	}
}

onMounted(() => {
	slideInterval.value = setInterval(() => {
		setNextSlide();
	}, 7000);
});

onUnmounted(() => {
	clearInterval(slideInterval.value);
});
</script>
